/**
 * A driver's name with the call and WhatsApp links the board offers for following up a no-show
 * (PLAN §3.6, §7). Nothing about the call is recorded. A stored phone that is not 233 then nine
 * digits is shown as stored, with no link; a driver with no app login is marked, since the app's
 * badge and card cannot reach them.
 */

import { MessageCircle, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { driverPhoneLinks } from '@/lib/inspections';

interface DriverContactProps {
    name: string | null;
    phone: string | null;
    hasLogin: boolean | null;
    /** Bigger touch targets for the phone cards. */
    large?: boolean;
    className?: string;
}

export function NoLoginMarker() {
    return (
        <span className="inline-flex items-center rounded border border-dashed border-muted-foreground/40 px-1.5 py-px text-[11px] text-muted-foreground">
            no app login
        </span>
    );
}

export function DriverContact({ name, phone, hasLogin, large, className }: DriverContactProps) {
    const links = driverPhoneLinks(phone);
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const linkClass = cn(
        'inline-flex items-center justify-center rounded-md border bg-background text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        large ? 'h-10 gap-1.5 px-3 text-sm' : 'h-7 w-7',
    );
    return (
        <div className={cn('min-w-0', className)}>
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate font-medium">{name ?? 'No driver name'}</span>
                {hasLogin === false && <NoLoginMarker />}
            </div>
            {links && (
                <div className={cn('flex flex-wrap items-center', large ? 'mt-2 gap-2' : 'mt-1 gap-1.5')}>
                    {links.tel ? (
                        <>
                            <a href={links.tel} onClick={stop} className={linkClass} aria-label={`Call ${name ?? 'the driver'} on ${links.display}`}>
                                <Phone className="h-3.5 w-3.5" aria-hidden />
                                {large && 'Call'}
                            </a>
                            <a
                                href={links.whatsapp!}
                                onClick={stop}
                                target="_blank"
                                rel="noreferrer"
                                className={linkClass}
                                aria-label={`WhatsApp ${name ?? 'the driver'} on ${links.display}`}
                            >
                                <MessageCircle className="h-3.5 w-3.5" aria-hidden />
                                {large && 'WhatsApp'}
                            </a>
                            <span className="text-xs tabular-nums text-muted-foreground">{links.display}</span>
                        </>
                    ) : (
                        <span className="text-xs tabular-nums text-muted-foreground" title="Not a 233 number, so no link">
                            {links.display}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
